import { createAutoAnimatePlugin } from '@formkit/addons';
import { en, fr, es } from '@formkit/i18n';
import { createProPlugin, datepicker, toggle, taglist, dropdown, currency, autocomplete } from '@formkit/pro';
import { generateClasses } from '@formkit/themes';
import { plugin, defaultConfig } from '@formkit/vue';
import { dropdown as dropdownStyles } from '~/boot/formkit-theme/families/dropdown';
import { global } from '~/boot/formkit-theme/global';
import type { App } from 'vue';

const datepickerStyle = {
	pickerHeader: 'flex items-center justify-center col-start-2 col-end-2',
	pickerButton: 'appearance-none cursor-pointer px-3 py-1 border-2 rounded-md mx-1 hover:border-blue-500',
	pickerInput:
		'flex items-center justify-center cursor-pointer rounded-md bg-gray-50 aria-selected:bg-blue-500 aria-selected:text-white focus:outline focus:outline-2 focus:outline-blue-500 focus:outline-offset-2 focus:bg-white focus:text-black data-[is-extra=true]:opacity-25  formkit-disabled:cursor-default formkit-disabled:pointer-events-none',
};

const proPlugin = createProPlugin('fk-510e055329', { datepicker, toggle, taglist, dropdown, currency, autocomplete });

export function setupFormKit(app: App<Element>): void {
	app.use(
		plugin,
		defaultConfig({
			plugins: [createAutoAnimatePlugin(), proPlugin],
			locale: localStorage.getItem('locale') || 'en',
			locales: { fr, en, es },
			config: {
				classes: generateClasses({
					'global': global,
					'family:dropdown': dropdownStyles,
					'dropdown': {
						...dropdownStyles,
						selector: 'flex w-full justify-between items-center',
						selections: 'flex gap-1',
						inner:
							'text-sm relative px-3 py-2 flex items-center border border-gray-300 focus-within:ring-blue-500 focus-within:ring-2 rounded-md',
					},
					'family:text': {
						inner:
							'flex items-center border border-gray-300 focus-within:ring-blue-500 focus-within:ring-2 [&>label:first-child]:focus-within:text-blue-500 rounded-md',
						input: 'w-full px-3 py-2 border-none text-base sm:text-sm placeholder-gray-400',
					},
					'autocomplete': {
						inner:
							'text-sm relative !p-0 flex items-center border border-gray-300 focus-within:ring-blue-500 focus-within:ring-2 rounded-md',
						input: 'px-3 py-2 text-sm',
					},
					'family:date': {
						inner:
							'flex items-center border border-gray-300 focus-within:ring-blue-500 focus-within:ring-2 [&>label:first-child]:focus-within:text-blue-500 rounded-md',
						input: 'w-full px-3 py-2 border-none text-base sm:text-sm text-gray-700 placeholder-gray-400',
					},
					'file': {
						inner:
							'mt-1 py-2 px-3 relative flex items-center justify-between w-full border border-gray-300 rounded-md sm:text-sm formkit-errors:border-red-500',
						input:
							'z-10 absolute inset-0 cursor-pointer text-transparent file:appearance-none file:text-0 file:border-none file:p-0 rounded-md focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400 placeholder:text-slate-400',
						noFiles: 'block text-sm font-medium text-gray-700',
						fileList: 'w-full',
						fileItem: 'block text-sm font-medium text-gray-700 flex items-center',
						fileRemove: 'relative ml-auto text-blue-500 text-sm z-20',
					},
					'checkbox': {
						label: '$reset ml-3 flex-grow font-medium text-gray-700 text-sm',
						outer: 'w-full',
						inner: '$reset',
						wrapper: 'flex items-center',
						input: '$reset form-checkbox border-gray-300 rounded text-blue-600 transition duration-150 ease-in-out',
					},
					'radio': {
						label: 'ml-3 !mb-0 flex-grow font-medium text-gray-700',
						outer: 'w-full',
						inner: '$reset',
						wrapper: 'flex items-center',
						input: '$reset border-gray-300 rounded-full text-blue-600 transition duration-150 ease-in-out',
					},
					'select': {
						input:
							'block w-full px-3 py-2 text-base text-gray-700 border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:opacity-100 sm:text-sm placeholder:text-gray-400 placeholder:text-slate-400',
					},
					'datepicker': {
						inner: 'relative',
						overlayPlaceholder: 'text-sm text-gray-500',
						panelWrapper: `
							group/panel
							absolute
							min-w-[20em]
							top-[calc(100%_+_0.5em)]
							left-0
							rounded-md
							border
							border-gray-300
							p-2
							bg-white
							z-10

							${'' /* mobile touch styles */}
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:!fixed
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:top-auto
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:max-w-none
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:bottom-0
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:left-0
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:w-full
						`,
						panelHeader: `
							grid
							grid-cols-[auto_1fr_auto]
							justify-center
							items-center
							border-b
							border-gray-300
							mb-4
							pb-4

							${'' /* mobile touch styles */}
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:grid-cols-[2.5em_1fr_2.5em_2.5em]
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:group-data-[panel=time]/panel:grid-cols-[2.5em_1fr_2.5em]
							[@media(max-width:431px)_and_(hover:none)]:group-[&:not([data-inline])]:group-data-[panel=month]/panel:grid-cols-[2.5em_1fr_2.5em]
						`,
						panelClose: `
							aspect-[1/1]
							border
							border-gray-300
							rounded-md
							flex
							items-center
							justify-center
							text-gray-700
							[&_svg]:w-[1.25em]

							${'' /* mobile touch styles */}
							[@media(max-width:431px)_and_(hover:none)]:group-data-[panel=time]/panel:col-start-3
							[@media(max-width:431px)_and_(hover:none)]:group-data-[panel=month]/panel:col-start-3
						`,
						panel: `
							flex
							justify-center
						`,
						input: `
							placeholder:text-gray-300
						`,
						monthsHeader: `
							flex
							items-center
							justify-center
							col-start-2
							col-end-2
						`,
						timeHeader: `
							flex
							items-center
							justify-center
							col-start-2
							col-end-2
						`,
						months: `
							grid
							grid-cols-3
							w-full
						`,
						month: `
							cursor-pointer
							m-2
							p-2
							text-center
							text-gray-700
							rounded-md
							bg-gray-200
							aria-selected:!bg-blue-300
							aria-selected:!text-white
							focus:outline
							focus:outline-2
							focus:outline-blue-300
							focus:outline-offset-2
							focus:bg-white
							focus:text-gray-700
							data-[is-extra=true]:opacity-25
							group-data-[disabled=true]:opacity-50
							group-data-[disabled=true]:cursor-default
							group-data-[disabled=true]:pointer-events-none
						`,
						yearsHeader: `
							flex
							items-center
							justify-center
							text-gray-700
							col-start-2
							col-end-2
						`,
						years: `
							grid
							grid-cols-5
							w-full
						`,
						year: `
							cursor-pointer
							text-xs
							text-center
							text-gray-700
							items-center
							m-2
							p-2
							rounded-md
							bg-gray-200
							aria-selected:!bg-blue-300
							aria-selected:!text-white
							focus:outline
							focus:outline-2
							focus:outline-blue-300
							focus:outline-offset-2
							focus:bg-white
							data-[is-extra=true]:opacity-25
							group-data-[disabled=true]:opacity-50
							group-data-[disabled=true]:cursor-default
							group-data-[disabled=true]:pointer-events-none
						`,
						weekDays: `
							grid
							grid-cols-7
						`,
						weekDay: `
							w-[2.25em]
							text-gray-700
							m-1
							rounded-md
							font-medium
							lowercase
						`,
						calendarWeeks: ``,
						week: `
							grid
							grid-cols-7
							group-data-[disabled=true]:opacity-50
							group-data-[disabled=true]:cursor-default
							group-data-[disabled=true]:pointer-events-none
						`,
						dayCell: `
							flex
							items-center
							justify-center
							text-center
							cursor-pointer
							text-gray-700
							size-[2.25em]
							m-1
							p-2
							rounded-md
							bg-gray-200
							aria-selected:bg-blue-300
							aria-selected:text-white
							focus:outline
							focus:outline-2
							focus:outline-blue-500
							focus:outline-offset-2
							focus:bg-white
							data-[is-extra=true]:opacity-25
							data-[disabled=true]:opacity-50
							data-[disabled=true]:cursor-default
							data-[disabled=true]:pointer-events-none
						`,
						timeInput: `
							w-full
							border-2
							text-gray-700
							border-gray-300
							rounded-md
							p-4
							my-4
							focus-visible:outline-blue-300
						`,
						daysHeader: `
							flex
							items-center
							justify-center
						`,
						prev: `
							mr-auto
							cursor-pointer
							px-4
							py-2
							hover:bg-gray-100
							rounded-md
							col-start-1
							col-end-1
							focus-visible:outline-none
							focus-visible:ring-2
							focus-visible:ring-blue-300
							focus-visible:ring-offset-2
						`,
						prevLabel: 'hidden',
						prevIcon: `
							size-5
							text-gray-500
						`,
						dayButton: `
							appearance-none
							text-gray-700
							cursor-pointer
							px-4
							py-2
							border
							border-gray-300
							rounded-md
							mx-1
							hover:border-blue-300
							focus-visible:outline-none
							focus-visible:ring-2
							focus-visible:ring-blue-300
							focus-visible:ring-offset-2
						`,
						monthButton: `
							appearance-none
							text-gray-700
							cursor-pointer
							px-4
							py-2
							border
							border-gray-300
							rounded-md
							mx-1
							hover:border-blue-300
							focus-visible:outline-none
							focus-visible:ring-2
							focus-visible:ring-blue-300
							focus-visible:ring-offset-2
						`,
						yearButton: `
							appearance-none
							text-gray-700
							cursor-pointer
							px-4
							py-2
							border
							border-gray-300
							rounded-md
							mx-1
							hover:border-blue-300
							focus-visible:outline-none
							focus-visible:ring-2
							focus-visible:ring-blue-300
							focus-visible:ring-offset-2
						`,
						next: `
							ml-auto
							cursor-pointer
							px-4
							py-2
							rounded-md
							hover:bg-gray-100
							hover:rounded
							col-start-3
							col-end-3
							focus-visible:outline-none
							focus-visible:ring-2
							focus-visible:ring-blue-300
							focus-visible:ring-offset-2
						`,
						nextLabel: `
							hidden
						`,
						nextIcon: `
							size-5
							text-gray-500
						`,
						openButton: `
							flex
							items-center
							justify-center
							appearance-none
							border-0
							bg-transparent
							flex
							px-4
							self-stretch
							cursor-pointer
							focus-visible:outline-none
							focus-visible:ring-2
							focus-visible:ring-blue-300
							focus-visible:ring-offset-2
						`,
						calendarIcon: `
							size-5
							text-gray-500
						`,
					},
					'toggle': {
						altLabel: 'text-sm font-medium text-gray-700',
						inner: '$reset inline-block mr-2',
						input: 'peer absolute opacity-0 pointer-events-none',
						innerLabel: 'text-[10px] font-bold absolute left-full top-1/2 -translate-x-full -translate-y-1/2 px-1',
						thumb: 'relative left-0 aspect-square rounded-full transition-all w-5 bg-white border border-gray-300',
						track:
							'bg-gray-50 border border-gray-300 peer-checked:border-transparent p-0.5 min-w-[3em] relative rounded-full transition-all peer-checked:bg-blue-500 peer-checked:[&>div:last-child]:border-blue-500 peer-checked:[&>div:last-child]:left-full peer-checked:[&>div:last-child]:-translate-x-full peer-checked:[&>div:first-child:not(:last-child)]:left-0 peer-checked:[&>div:first-child:not(:last-child)]:translate-x-0',
						valueLabel: 'text-sm font-medium text-gray-700',
						wrapper: 'flex flex-wrap items-center',
					},
					// inherits family:dropdown classes
					'taglist': {
						inner: 'pr-0 pl-0',
						tags: 'pl-2',
						input: 'inline-block min-w-[1em] !w-[0%]',
						listboxButton: 'ml-auto shrink-0',
						tagRemove: 'cursor-pointer',
						tagWrapper:
							'[&.formkit-dropZone_.formkit-tag]:opacity-50 [&.formkit-touchDropZone_.formkit-tag]:opacity-50',
					},
					'textarea': {
						input:
							'py-2 px-3 relative flex items-center focus:ring-2 focus:ring-blue-500 justify-between w-full border border-gray-300 rounded-md sm:text-sm formkit-errors:border-red-500',
					},
				}),
			},
		})
	);
}
